<template>
  <div id="dashboard-map" class="panel">
    <div class="panel-block no-padding">
      <div class="panel-block map-sidebar">
        <LayerSelect
          class="map-sidebar-item"
          v-on:selectedLayer="onSelectedLayer"
        />
      </div>
      <WeatherIcon v-if="showWeatherIcon" />
      <mapbox
        :access-token="accessToken"
        :map-options="mapOptions"
        @map-init="mapInitialized"
        @map-load="mapLoaded"
        @map-moveend="mapUpdate"
        @map-click="mapClicked"
      >
      </mapbox>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import Mapbox from "mapbox-gl-vue";
import LayerSelect from "./LayerSelect";
import WeatherIcon from "./WeatherIcon";
import config from "../../../config/app.config";

import constants from "../../constants";
import Marker from "../../assets/device_marker.png";
import TrackerMarker from "../../assets/tracker_marker.png";
import EventMarker from "../../assets/event_marker.png";
import VrMarker from "../../assets/vr_marker.png";
// import AnteaMarker from '../../assets/sensor_marker.png'

let deviceMarker = new Image();
let trackerMarker = new Image();
let eventMarker = new Image();
deviceMarker.src = Marker;
trackerMarker.src = TrackerMarker;
eventMarker.src = EventMarker;
let vrMarker = new Image();
vrMarker.src = VrMarker;

/**
 *  We have to keep the map reference outside vue 'data' object
 *  otherwise the mapbox styles break
 */
let mapRef = {};
let popup = null;

export default {
  name: "Map",
  props: {
    options: Object,
    filter: Object,
    events: Object
  },
  components: {
    Mapbox,
    LayerSelect,
    WeatherIcon
  },
  data() {
    return {
      accessToken: config.mapboxToken,
      mapOptions: {
        style: config.mapStyles.light,
        center: [config.defaultMapCenter.lon, config.defaultMapCenter.lat],
        zoom: config.defaultMapZoom
      },
      baseUrl: config.parkingBaseUrl,
      canUpdateDatesource: false,
      trackerRoutes: [],
      parking: {
        places: null,
        timeseries: []
      },
      smileys: {
        type: "FeatureCollection",
        features: []
      }
    };
  },
  computed: {
    ...mapGetters({
      selectedNavigationTabIndex: "navigationTabIndex",
      selectedEventIndex: "dataEventSelectedEventIndex",
      eventsFeatureCollection: "dataEventCollectionFeature",
      mapDataUpdate: "mapDataUpdate",
      showWeatherIcon: "showWeatherIcon"
    })
  },
  watch: {
    selectedNavigationTabIndex(val) {
      const activeTab = this.$store.state.navigation.tabs[val];
      const center = [activeTab.location.lng, activeTab.location.lat];
      if (mapRef && mapRef.loaded()) {
        // Clear selected periods.
        this.$store.commit("clearPresetActivePreset");
        this.$store.commit("clearDataTracker");

        // Load new data in map.
        this.$store.dispatch("fetchEvents");
        this.$store.dispatch("fetchPresets", activeTab.name);
        this.$store.dispatch("fetchCurrentWeather", {
          location: activeTab.name
        });

        // Fly to new location.
        mapRef.flyTo({ center: center });
      }
    },
    selectedEventIndex(val) {
      if (!val) {
        return;
      }
      const layers = this.$store.state.map.data.layers;
      for (let i = 0; i < layers.length; i++) {
        if (layers[i].id === constants.MAP_LAYER_EVENT_ID) {
          const layerControl = this.$store.state.map.controls.layers;
          for (let j = 0; j < layerControl.length; j++) {
            if (layerControl[j].id === constants.MAP_LAYER_EVENT_ID) {
              if (layerControl[j].visible) {
                const selectedEvent = layers[i].source.data.features[val];
                // Clear previous popup
                if (popup) {
                  popup.remove();
                }
                // Populate the popup.
                let popupContent = this.createPopUpContent(
                  selectedEvent,
                  constants.MAP_LAYER_EVENT_ID
                );
                // eslint-disable-next-line
                popup = new mapboxgl.Popup()
                  .setLngLat(selectedEvent.geometry.coordinates)
                  .setHTML(popupContent)
                  .addTo(mapRef);
              }
              break;
            }
          }
          break;
        }
      }
    },
    eventsFeatureCollection(val) {
      const source = mapRef.getSource(constants.MAP_LAYER_EVENT_ID);
      if (source) {
        source.setData(val);
      }
    },
    mapDataUpdate(val) {
      if (val) {
        // const source = mapRef.getSource(constants.MAP_LAYER_PARKING_ID)
        const layers = this.$store.state.map.data.layers;
        for (var i = 0; i < layers.length; i++) {
          const source = mapRef.getSource(layers[i].id);
          if (source) {
            source.setData(layers[i].source.data);
          }
        }
        this.$store.commit("setMapDataUpdate", false);
      }
    }
  },
  methods: {
    mapInitialized(map) {
      mapRef = map;
    },
    mapLoaded(map) {
      // Props of the map component do not seem to be reactive.
      // Manually setting the center and zoom here.
      map.setZoom(this.options.zoom);
      map.setCenter(this.options.center);

      // Set bounds for geospatial query.
      let bounds = {
        ne: map.getBounds().getNorthEast(),
        sw: map.getBounds().getSouthWest()
      };
      this.$store.commit("setMapBounds", bounds);

      // Create marker images.
      map.addImage(constants.EVENT_MARKER_IMAGE_ID, eventMarker);
      map.addImage(constants.VR_MARKER_IMAGE_ID, vrMarker);
      // map.addImage(constants.ANTEA_MARKER_IMAGE_ID, anteaMarker)

      const layers = this.$store.state.map.data.layers;
      for (let i = 0, len = layers.length; i < len; i++) {
        map.addLayer(layers[i]);
      }

      const layersControl = this.$store.state.map.controls.layers;
      for (let i = 0, len = layersControl.length; i < len; i++) {
        let visibility = layersControl[i].visible ? "visible" : "none";
        map.setLayoutProperty(layersControl[i].id, "visibility", visibility);
        if (layersControl[i].items) {
          for (let j = 0, len = layersControl[i].items.length; j < len; j++) {
            let visibility = layersControl[i].items[j].visible
              ? "visible"
              : "none";
            map.setLayoutProperty(
              layersControl[i].items[j].id,
              "visibility",
              visibility
            );
          }
        }
      }

      this.canUpdateDatesource = true;
    },
    mapUpdate(map, e) {
      // console.log('Map udpate: ', this.canUpdateDatesource);
      if (e.target !== null) {
        // Manually update datasource. God knows why...
        if (this.canUpdateDatesource) {
          // Set bounds for geospatial query.
          let bounds = {
            ne: e.target.getBounds().getNorthEast(),
            sw: e.target.getBounds().getSouthWest()
          };
          this.$store.commit("setMapBounds", bounds);

          // Update data sources
          this.updateDataSources();
        }

        // Emit update if we have a center.
        if (e.target.getCenter() !== null) {
          this.$emit("mapUpdated", {
            center: [e.target.getCenter().lng, e.target.getCenter().lat],
            zoom: e.target.getZoom()
          });
        }
      }
    },
    updateDataSources() {
      // eslint-disable-next-line
      console.log("Update Data Sources");
      this.$store.dispatch("fetchEvents");
      this.$store.dispatch("fetchParkingPlaces");
      // this.$store.dispatch('fetchTrackers')

      // let location = this.$store.getters.navigationActiveTab.name
      // this.$store.dispatch('fetchCurrentWeather', { 'location': location })
      // this.$store.dispatch('fetchSurveys', { 'location': location })
    },
    mapClicked(map, e) {
      // Always clear selected event.
      this.$store.commit("setDataEventsSelected", {});

      // Find clicked feature
      const features = map.queryRenderedFeatures(e.point, {
        layers: [
          constants.MAP_LAYER_EVENT_ID,
          constants.MAP_LAYER_PARKING_ID,
          constants.MAP_LAYER_TRAFFIC_ID,
          constants.MAP_LAYER_VR_ID,
          // constants.MAP_LAYER_ANTEA_ID,
          constants.MAP_LAYER_SURVEY_ID
        ]
      });
      if (features.length > 0) {
        // Let's pick the first one (the array should only have one item).
        const feature = features[0];
        this.$emit("selectedFeature", feature);

        // When an event is selected on the map update the store so the
        // selected event is highlighted in the event list.
        if (feature.layer.id === constants.MAP_LAYER_EVENT_ID) {
          this.$store.commit("setDataEventsSelected", feature);
        }
        if (feature.layer.id !== constants.MAP_LAYER_SURVEY_ID) {
          // Add a popup to the map
          this.addPopUp(map, feature);
        }
      }
    },
    addPopUp(map, feature) {
      // Create popup
      let popupContent = this.createPopUpContent(feature, feature.layer.id);
      // eslint-disable-next-line
      popup = new mapboxgl.Popup()
        .setLngLat(feature.geometry.coordinates)
        .setHTML(popupContent)
        .addTo(map);
    },
    createPopUpContent(feature, layerId) {
      let popupContent = "";
      if (layerId === constants.MAP_LAYER_EVENT_ID) {
        popupContent =
          '<div id="vue-popup-content">' +
          '<p><em style="display:inline-block; width: 50px;">Name:</em> ' +
          feature.properties.name +
          "</p>" +
          '<p><em style="display:inline-block; width: 50px;">Location:</em> ' +
          feature.properties.locatie +
          "</p>" +
          '<p><em style="display:inline-block; width: 50px;">Date:</em> ' +
          moment(feature.properties.startdatum).format("YYYY-MM-DD") +
          "</p>" +
          "</div>";
      } else if (feature.layer.id == constants.MAP_LAYER_PARKING_ID) {
        popupContent =
          '<div id="vue-popup-content">' +
          '<p><em style="display:inline-block; width: 120px;">Total aantal plaatsen:</em> ' +
          feature.properties.totalSpots +
          "</p>" +
          '<p><em style="display:inline-block; width: 120px;">Beschikbare plaatsen:</em> ' +
          feature.properties.availableSpots +
          "</p>";
        ("</div>");
      } else if (feature.layer.id == constants.MAP_LAYER_TRACKERS_ID) {
        popupContent =
          '<div id="vue-popup-content">' +
          '<p><em style="display:inline-block; width: 40px;">Name:</em> ' +
          feature.properties.name +
          "</p>" +
          '<p><em style="display:inline-block; width: 40px;">Date:</em> ' +
          moment(feature.properties.timestamp).format("YYYY-MM-DD") +
          "</p>" +
          '<p><em style="display:inline-block; width: 40px;">Time:</em> ' +
          moment(feature.properties.timestamp).format("HH:mm:ss") +
          "</p>" +
          "</div>";
      } else if (feature.layer.id == constants.MAP_LAYER_VR_ID) {
        popupContent =
          '<div id="vue-popup-content">' +
          '<p><em style="display:inline-block; width: 75px;">Omschrijving:</em> ' +
          feature.properties.description +
          "</p>" +
          "</div>";
      } else if (feature.layer.id == constants.MAP_LAYER_TRAFFIC_ID) {
        popupContent =
          '<div id="vue-popup-content">' +
          '<p><em style="display:inline-block; width: 65px;">Description:</em> ' +
          feature.properties.description +
          "</p>" +
          '<p><em style="display:inline-block; width: 65px;">Count:</em> ' +
          feature.properties.detectionCount +
          "</p>" +
          "</div>";
      }
      // else if(feature.layer.id == constants.MAP_LAYER_ANTEA_ID){
      //   popupContent =
      //     '<div id="vue-popup-content">' +
      //     '<p><em style="display:inline-block; width: 45px;">Locatie:</em> ' + feature.properties.name + '</p>' +
      //     '</div>'
      // }
      return popupContent;
    },
    onSelectedLayer(layer) {
      mapRef.setLayoutProperty(
        layer.id,
        "visibility",
        layer.visible ? "none" : "visible"
      );
    }
  }
};
</script>

<style scoped>
.no-padding {
  padding: 0;
}

#map {
  /* height: calc(100vh - 300px); */
  height: 650px;
  width: 100vw;
}

.map-sidebar {
  /* height: calc(100vh - 300px); */
  height: 650px;
  border-left: 0;
  border-bottom: 0;
  border-top: 0;
  flex-flow: column;
  width: 570px;
}
.map-sidebar-items {
  align-self: flex-start;
  padding-bottom: 10px;
}

#dashboard-map .panel-block {
  position: relative;
}

.weather-icon {
  position: absolute;
  top: 10px;
  left: 300px;
  z-index: 1;
}
</style>
